import i18next from 'i18next';
//import { DateTime } from 'luxon';
import { Target, TargetFrequency, TargetType } from 'stores/workflowStores/finances/targets/model';
import { Deposit, DepositFrequency, DepositType } from 'stores/workflowStores/finances/depositsStore/model';
import { DepositBySubtype, EventType, ExtDeposit, ExtReserve, ExtTarget } from './model';
import { Reserve } from 'stores/workflowStores/finances/newReservesStore/model';
import { AccountEditPlan, ChartComputedData } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { rentIsZero } from 'helpers/targets';
import EventLabel from './EventLabel';
import { renderToString } from 'react-dom/server';
import { compareChartDates, compareISODates } from 'helpers/helpers';
import { Currency, EdwardColors } from 'enums';
import { altColor, dateColor, depositColor, extProdColor, loanColor, reserveColor, targetColor } from './Chart';
import { XAxisPlotLinesOptions } from 'highcharts';
import { AmountValue } from 'common/interfaces';
import {
  ExternalProductData,
  ExtProductCategory,
  ExtProductFreq,
  ExtProductUsage,
} from 'pages/Accounts/Edit/components/ExternalProducts/model';
import colors from '../../../../../components/PortfolioTables/colors';

export const generateRepeatedEvents = (events: Target[] | Deposit[] | ExternalProductData[]) => {
  /* eslint-disable-next-line */
  const output = Array();
  events.forEach((e: Target | Deposit | ExternalProductData) => {
    output.push(e);
    if (
      e.frequency &&
      [TargetFrequency.REPEAT_EVERY, DepositFrequency.REPEAT_EVERY, ExtProductFreq.REPEAT_EVERY].includes(e.frequency)
    ) {
      const g: ExtTarget | ExtDeposit = JSON.parse(JSON.stringify(e));
      const startYear = e.since ? Number(/(\d{4})/.test(e.since) ? e.since.match(/(\d{4})/)![1] : 0) : 0;
      const endYear = e.until ? Number(/(\d{4})/.test(e.until) ? e.until.match(/(\d{4})/)![1] : 0) : 0;
      const monthDay = e.since ? (/(-\d{2}-\d{2})/.test(e.since) ? e.since.match(/(-\d{2}-\d{2})/)![1] : '') : '';
      const repeat = Number(e.repeatYears);
      for (let i = startYear + repeat; i <= endYear; i += repeat) {
        g.date = `${i}${monthDay}`;
        output.push(JSON.parse(JSON.stringify(g)));
      }
    }
  });
  return output;
};

export const transformReserves = (reserves: Reserve[], rentStart: string | undefined, endDate: string) => {
  /* eslint-disable-next-line */
  const output = Array();
  reserves.forEach((e: Reserve) => {
    const g: ExtReserve = JSON.parse(JSON.stringify(e));
    switch (g.horizon) {
      case 'RENT_START':
        if (rentStart) {
          g.date = rentStart;
        } else {
          g.id = undefined; // remove from events if no rent set
        }
        break;
      case 'ALL_TIME':
        g.date = endDate;
        break;
      default:
        g.date = g.until;
        break;
    }
    output.push(g);
  });
  return output;
};

const SCALE_MOBILE = 0.8;
//const LABEL_POINT_SIZE = 10;
const LABEL_MIN_SIZE = 45;
const LABEL_MAX_SIZE = 70;
const LABEL_MIN_SIZE_PDF = 30;
const LABEL_MAX_SIZE_PDF = 50;
const LABEL_DEV_MIN_SIZE = 15;
const LABEL_DEV_MAX_SIZE = 30;
const LABEL_DEV_MIN_SIZE_PDF = 10;
const LABEL_DEV_MAX_SIZE_PDF = 20;
const LABEL_ZEROED_SIZE = 16;

interface DetailDataset {
  [key: string]: [{ [key: string]: AmountValue }];
}

const findInflatedValue = (dataSet: DetailDataset, date: string, id: string) => {
  const item = dataSet[date]?.find((item) => Object.keys(item)[0] === id);
  return item ? item[id].value : undefined;
};

export const assembleEvents = (
  planData: AccountEditPlan,
  metaData: ChartComputedData,
  dates: string[],
  totals: any[],
  currency: Currency,
  hoverClass: string,
  mobile: boolean,
  readOnly?: boolean,
  original?: boolean,
  pdf?: boolean,
): XAxisPlotLinesOptions[] => {
  const { depositData, targetData, reserveData, personType, rentData, externalProductData } = planData;
  const targetDetails = planData.chart.dataset?.find((ds) => ds.title === 'targets')?.detailData as
    | DetailDataset
    | undefined;
  const depositDetails = planData.chart.dataset?.find((ds) => ds.title === 'deposits')?.detailData as
    | DetailDataset
    | undefined;

  const misc = [
    {
      rent: true,
      type: rentData && !rentIsZero(rentData) ? 'PORTFOLIO_RENT' : null, // remove from events if no rent set
      title: i18next.t('chart_rent_start_plotline_label'),
      pos: planData.chart.metadata.rentStartIndex,
      since: rentData?.start,
      frequency: 'MONTHLY',
      rentData: rentData,
    },
    {
      type: metaData.displaySetValue && metaData.showRange ? 'DATE' : null, // remove from events if no date set
      title: i18next.t('accountPlan_valueDisplay'),
      date: metaData.displayDate,
      value: { value: metaData.displayValue },
      frequency: 'SINGLE',
    },
  ];

  const allEvents = [
    ...generateRepeatedEvents(depositData.deposits),
    ...generateRepeatedEvents(targetData.targets.filter((t) => t.enabled)), // only enabled targets
    ...generateRepeatedEvents(
      externalProductData.externalProducts.filter(
        (p) => p.frequency !== ExtProductFreq.INFINITE && (p.usage !== ExtProductUsage.NONE || p.category === ExtProductCategory.LOAN),
      ),
    ),
    ...transformReserves(reserveData.reserves, rentData?.start, metaData.endDate!),
    ...misc,
  ]
    .filter((e) => !!e.type || !!e.id)
    .sort((a, b) => compareChartDates(a.date || a.since!, b.date || b.since!));

  // TODO: make a helper fn; step, min, max
  let minAmount = 0;
  let maxAmount = 0;
  for (const event of allEvents) {
    const value = event.type === EventType.DATE ? null : event.value?.value;
    if (value) {
      minAmount = !minAmount || value < minAmount ? value : minAmount;
      maxAmount = !maxAmount || value > maxAmount ? value : maxAmount;
    }
  }
  const labelMin = pdf ? LABEL_MIN_SIZE_PDF : LABEL_MIN_SIZE;
  const labelMax = pdf ? LABEL_MAX_SIZE_PDF : LABEL_MAX_SIZE;

  const step = Math.max((maxAmount - minAmount) / (labelMax - labelMin), 1);

  return allEvents.map((e) => {
    const extProd = 'category' in e;
    const finInst = 'financialInstitution' in e;
    const target = 'fulfillment' in e || 'rent' in e || e.usage === ExtProductUsage.EXTERNAL;
    const reserve = 'horizon' in e;
    const date = e.type === EventType.DATE;
    //const otherExtProd = 'category' in e && [ExtProductCategory.PROPERTY, ExtProductCategory.LOAN].includes(e.category);
    const loan = 'category' in e && e.category === ExtProductCategory.LOAN;
    const property = 'category' in e && e.category === ExtProductCategory.PROPERTY;

    const icon = target || (extProd && !finInst) ? e.type : reserve ? 'RESERVE_END' : date ? 'DATE' : e.frequency;
    const value =
      'pos' in e
        ? e.pos
        : dates.findIndex((a: string) => (loan ? a === e.periodic.until : e.date ? a === e.date : a === e.since));
    const pos = value / dates.length;
    const amount = e.type === EventType.DATE ? null : e.value?.value;
    const size =
      (mobile ? SCALE_MOBILE : 1) *
      (e.type === 'PORTFOLIO_RENT'
        ? labelMax
        : loan
        ? LABEL_ZEROED_SIZE
        : amount
        ? labelMin + (amount - minAmount) / step
        : Math.round(labelMin + (labelMax - labelMin) / 2));

    const totalAtPos = totals.find(
      (d: [string, number]) => (e.date && d[0] === e.date) || (!e.date && d[0] === e.since),
    );

    const deposit = !target && !reserve && !date;

    const inflatedValue =
      ((deposit && depositDetails) || (target && targetDetails) || (extProd && e.usage === ExtProductUsage.TARGET)) &&
      planData.chart.inflation.enabled === false &&
      e.inflation?.enabled
        ? findInflatedValue(target ? targetDetails! : depositDetails!, e.date || e.since, e.id)
        : undefined;

    const getColor = (): string => {
      if (original) return altColor;
      if (loan) return loanColor;
      if (property) return extProdColor;
      if (date) return dateColor;
      if (target) return targetColor;
      if (reserve) return reserveColor;
      return depositColor; // Default color
    };

    const getEventType = (): EventType => {
      if (property) return EventType.PROPERTY;
      if (loan) return EventType.LOAN;
      if (target) return EventType.TARGET;
      if (reserve) return EventType.RESERVE;
      if (date) return EventType.DATE;
      return EventType.DEPOSIT; // Default type
    };

    return {
      width: 0,
      dashStyle: 'ShortDash',
      value,
      className: 'custom-plot-line',
      color: getColor(),

      label: {
        verticalAlign: 'top',
        rotation: 0,
        useHTML: true,
        textAlign: 'center',
        x: 0,
        y: -size,
        className: hoverClass,
        text: renderToString(
          <EventLabel
            id={e.id}
            extProduct={extProd}
            type={getEventType()}
            iconType={icon as TargetType | DepositType | 'RESERVE_END' | 'DATE'}
            icon={finInst && !loan ? e.financialInstitution.icon : undefined}
            color={property ? colors[`PROPERTY_TYPE_${e.type}`] : finInst && !loan ? e.financialInstitution.colour : undefined}
            title={loan ? e.financialInstitution?.title : finInst ? e.financialInstitution.title + ' (' + e.title + ')' : e.title}
            usage={loan ? e.usage : undefined}
            targetTitle={loan ? e.title : e.targetTitle}
            value={loan? e.periodic.value : e.value}
            inflatedValue={inflatedValue}
            inflation={e.inflation}
            since={e.since}
            date={e.date}
            until={loan? e.periodic.until : e.until}
            horizon={e.horizon}
            bucket={e.bucket}
            align={pos > 0.85 ? 'right' : pos < 0.08 ? 'left' : 'center'}
            pos={value}
            repeat={e.frequency === 'YEARLY' || e.frequency === 'MONTHLY'}
            repeatYears={e.repeatYears}
            frequency={e.frequency}
            rent={e.rent}
            rentData={e.rentData}
            readOnly={readOnly}
            personType={personType}
            currency={currency}
            size={size}
            altColors={!!original}
            totalAtPos={totalAtPos ? totalAtPos[1] : undefined}
            pdf={pdf}
          />,
        ),
      },
      zIndex: 5,
    };
  });
};

export interface HistoricalEvent {
  value: AmountValue;
  date: string;
  title?: string;
  subtype?: DepositBySubtype;
  failedDeposit?: boolean;
  failed?: boolean; // Added this line
}

export const assembleHistoricalEvents = (
  targets: HistoricalEvent[],
  withdrawals: HistoricalEvent[],
  singleDeposits: HistoricalEvent[],
  periodicDeposits: HistoricalEvent[],
  investmentTotals: HistoricalEvent[] | { date: string; value: number }[], // investments | percentages
  currency: Currency,
  hoverClass: string,
  mobile: boolean,
  filterEvents: EventType[],
  pdf?: boolean,
): XAxisPlotLinesOptions[] => {
  const totals = investmentTotals.map((i) => ({
    time: i.date, //DateTime.fromISO(i.time).toFormat('yyyy-MM-dd'),
    value: typeof i.value === 'number' ? i.value * 100 : i.value.value, // number is percentage
  }));

  const allEvents = [
    ...targets.map((e, i) => ({
      ...e,
      id: `t${i}`,
      type: EventType.TARGET,
      time: e.date, //DateTime.fromISO(e.time).toFormat('yyyy-MM-dd'),
    })),
    ...withdrawals.map((e, i) => ({
      ...e,
      id: `w${i}`,
      type: EventType.WITHDRAWAL,
      time: e.date, //DateTime.fromISO(e.time).toFormat('yyyy-MM-dd'),
    })),
    ...singleDeposits.map((e, i) => ({
      ...e,
      id: `s${i}`,
      type: EventType.DEPOSIT,
      time: e.date, //DateTime.fromISO(e.time).toFormat('yyyy-MM-dd'),
      subtype: e.subtype,
      failedDeposit: e?.failed,
    })),
    ...periodicDeposits.map((e, i) => ({
      ...e,
      id: `p${i}`,
      type: EventType.PERIODIC_DEPOSIT,
      time: e.date, //DateTime.fromISO(e.time).toFormat('yyyy-MM-dd'),
      subtype: e.subtype,
      failedDeposit: e?.failed,
    })),
  ]
    .filter((e) => filterEvents.includes(e.type))
    .sort((a, b) => compareISODates(a.time, b.time));

  // TODO: make a helper fn; step, min, max
  let minAmount = 0;
  let maxAmount = 0;
  for (const event of allEvents) {
    // FIXME: types to enum
    const value = event.value?.value;
    if (value) {
      minAmount = !minAmount || value < minAmount ? value : minAmount;
      maxAmount = !maxAmount || value > maxAmount ? value : maxAmount;
    }
  }
  const amtDiff = minAmount / maxAmount;
  const minSize = pdf ? LABEL_DEV_MIN_SIZE_PDF : LABEL_DEV_MIN_SIZE;
  const maxSize = pdf ? LABEL_DEV_MAX_SIZE_PDF : LABEL_DEV_MAX_SIZE;
  const labelMin = amtDiff <= 0.5 ? minSize : ((minSize + maxSize) / 2) * 0.9;
  const labelMax = amtDiff <= 0.5 ? maxSize : (minSize + maxSize) / 2;

  const step = Math.max((maxAmount - minAmount) / (labelMax - labelMin), 1);

  return allEvents.map((e) => {
    const icon = e.type;
    //const value = totals.findIndex((t) => t.time === e.time);
    const value = Math.max(totals.findIndex((t) => t.time === e.time) - 1, 0);
    const pos = value / totals.length;
    const amount = e.value?.value;
    const size =
      (mobile ? SCALE_MOBILE : 1) *
      (e.type === EventType.PERIODIC_DEPOSIT
        ? 12
        : amount
        ? labelMin + (amount - minAmount) / step
        : Math.round(labelMin + (labelMax - labelMin) / 2));
    const totalAtPos = totals[value];

    return {
      width: 0,
      dashStyle: 'ShortDash',
      value: value,
      className: 'custom-plot-line',
      //color: date ? dateColor : target ? targetColor : reserve ? reserveColor : depositColor,

      label: {
        verticalAlign: 'top',
        rotation: 0,
        useHTML: true,
        textAlign: 'center',
        x: 0,
        y: -size / 2,
        className: hoverClass,
        text: renderToString(
          <EventLabel
            id={e.id}
            type={e.type}
            iconType={icon as TargetType | DepositType | 'RESERVE_END' | 'DATE'}
            title={e.title}
            value={e.value}
            date={e.time}
            align={pos > 0.85 ? 'right' : pos < 0.08 ? 'left' : 'center'}
            pos={value}
            currency={currency}
            size={size}
            totalAtPos={totalAtPos ? totalAtPos.value : undefined}
            pdf={pdf}
            historical
            subtype={e.subtype}
            failedDeposit={e.failedDeposit}
          />,
        ),
      },
      zIndex: 5,
    };
  });
};

export const offsetLabel = (
  label: Element,
  plotLine: Element,
  visible: boolean,
  yMin: number,
  yMax: number,
  areaHeight: number,
  spacingTop: number,
  chartStartX: number,
  chartStartY: number,
  totals: number[],
  tickPx: number,
  prevSize: number[],
  prevShift: number[],
  prevPos: number[],
) => {
  const c = label.children[0];
  const dev = c.getAttribute('data-dev') === 'true'; // developement graph
  const zeroed = c.getAttribute('data-zeroed') === 'true'; // sits on the x-axis
  const size = Number(c.getAttribute('data-size'));
  const index = Number(c.getAttribute('data-pos'));

  const pos = index * tickPx; // pos on the x axis in pixels
  const total = totals.length ? totals[index] : Number(c.getAttribute('data-total')); // stacked Y value || totals from BE
  const hcTranslate = 12; // 12 px is HC transform-origin value for labels

  const shift = zeroed
    ? areaHeight + hcTranslate + size / 2
    : yMax
    ? areaHeight - ((total - yMin) / (yMax - yMin)) * areaHeight + (dev ? hcTranslate : 0)
    : 0; // move from top (original pos) to the edge of chart drawing
  //console.log(`[${prevSize.length}]: total: ${total}, yMin:${yMin}, yMax:${yMax}, slice(${(total - yMin)}/${yMax - yMin}) = ${((total - yMin) / (yMax - yMin))}`);

  let offset = 0,
    tempShift = shift;
  let dotOverlap = false;

  if (prevSize.length) {
    for (let i = 0; i < prevSize.length; i++) {
      const pSize = prevSize[i];
      const pShift = prevShift[i];
      const pPos = prevPos[i];

      const labelOverlap =
        pos === pPos ||
        (pos - size / 2 <= pPos + pSize / (dev ? 2 : 4) &&
          tempShift + size >= pShift + pSize * 0.5 &&
          tempShift <= pShift + pSize * 0.5); //0.25
      offset = labelOverlap ? pSize * (dev ? 1.2 : 0.8) + shift - pShift : 0;
      tempShift -= offset;

      if (
        !dev &&
        shift + size + 16 + offset > pShift &&
        shift + size + 16 + offset < pShift + pSize &&
        shift < pShift + pSize &&
        pos - 5 > pPos - pSize / 2 &&
        pos + 5 < pPos + pSize / 2
      ) {
        dotOverlap = true;
      }
    }
  }

  prevShift.push(shift - offset);
  prevSize.push(size);
  prevPos.push(pos);

  const chartOverflow = shift - offset < size - spacingTop + hcTranslate;
  const tooltipOverflow = shift - offset - 70 < size - spacingTop; // 70 = hover/tooltip info height

  c.setAttribute('data-positioned', 'true');
  c.setAttribute(
    'style',
    visible
      ? `transform: translate(0, ${chartOverflow ? size - spacingTop + hcTranslate : shift - offset}px)`
      : 'display: none',
  ); // label
  c.setAttribute('data-chart-overflow', chartOverflow ? 'true' : 'false');
  c.setAttribute('data-tooltip-overflow', tooltipOverflow ? 'true' : 'false');
  c.children[0].setAttribute(
    'style',
    `transform: translate(0, ${
      chartOverflow ? shift + spacingTop - size - hcTranslate : offset - (dev ? hcTranslate : 0)
    }px)`,
  ); // dot
  c.children[0].setAttribute('class', dotOverlap || dev ? `hidden` : ``); // dot
  c.children[1].setAttribute(
    'style',
    `height: ${
      offset > 0 ? (chartOverflow ? shift + spacingTop - size + 4 : offset + 4 - (dev ? hcTranslate : 0)) : 0
    }px`,
  ); // plotline

  plotLine.setAttribute(
    'd',
    `M ${chartStartX + pos} ${chartStartY + shift - offset} L ${chartStartX + pos} ${chartStartY + shift}`,
  );
  plotLine.setAttribute('style', `opacity: 0.5`);

  //c.setAttribute('data-debug', `shift: ${shift}, offset: ${offset}`);

  return c;
};

export const devGraphLineColor = (portfolio: string) => {
  switch (portfolio) {
    case 'K1':
      return EdwardColors.MEDIUM;
    case 'K2':
      return EdwardColors.DARK;
    case 'K3':
      return EdwardColors.SECONDARY;
    default:
      return EdwardColors.BLUE;
  }
};
